import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

import { GIZLI_ENDPOINTLER } from '../constants/filters';

const API = process.env.REACT_APP_API_URL;
const SAYFA_BOYUTU = 20;

export const BOSH_FILTRELER = {
  musteri:   '',
  modul:     '',
  arama:     '',
  sure:      'bugun',
  baslangic: '',
  bitis:     '',
  sirala:    'zaman_desc',
};

async function apiCall(params, token) {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) p.set(k, String(v));
  });
  const res = await fetch(`${API}/logs?${p}`, {
    headers: { Authorization: token },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export function useLogs(filtreler = BOSH_FILTRELER) {
  const [allGroups, setAllGroups] = useState([]);
  const [total, setTotal]         = useState(0);
  const [sayfa, setSayfa]         = useState(1);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [lastFetch, setLast]      = useState(null);
  const timerRef                  = useRef(null);

  const filtreKey = JSON.stringify(filtreler);
  const prevFiltreRef = useRef(filtreKey);
  useEffect(() => {
    if (prevFiltreRef.current !== filtreKey) {
      prevFiltreRef.current = filtreKey;
      setSayfa(1);
    }
  }, [filtreKey]);

  const fetch_ = useCallback(async () => {
    if (!filtreler.musteri) {
      setAllGroups([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      // API'ye sadece desteklediği parametreleri gönder
      const apiParams = {
        musteri: filtreler.musteri,
        sirala:  filtreler.sirala,
        limit:   200,
      };
      // Zaman aralığı hesapla
      const { sure } = filtreler;
      if (sure === 'bugun') {
        const bugun = new Date();
        bugun.setHours(0, 0, 0, 0);
        apiParams.baslangic = bugun.toISOString();
      } else if (sure === 'dun') {
        const bugun = new Date();
        bugun.setHours(0, 0, 0, 0);
        const dun = new Date(bugun);
        dun.setDate(dun.getDate() - 1);
        apiParams.baslangic = dun.toISOString();
        apiParams.bitis = bugun.toISOString();
      } else if (typeof sure === 'number') {
        apiParams.sure = sure;
      }

      if (filtreler.baslangic) apiParams.baslangic = filtreler.baslangic;
      if (filtreler.bitis)     apiParams.bitis     = filtreler.bitis;

      // 4 log tipini paralel çek
      const [reqJson, procJson, valJson, errJson] = await Promise.all([
        apiCall({ tip: 'request',    ...apiParams }, token),
        apiCall({ tip: 'process',    ...apiParams }, token),
        apiCall({ tip: 'validation', ...apiParams }, token),
        apiCall({ tip: 'error',      ...apiParams }, token),
      ]);

      const requests    = reqJson.results ?? [];
      const processes   = procJson.results ?? [];
      const validations = valJson.results ?? [];
      const errors      = errJson.results ?? [];

      // requestId bazında grupla
      const map = new Map();

      const ensure = (rid) => {
        if (!map.has(rid)) {
          map.set(rid, {
            requestId: rid,
            request: null,
            process: [],
            validation: [],
            error: [],
          });
        }
        return map.get(rid);
      };

      requests.forEach(r => {
        if (!r.requestId || r.requestId === '-') return;
        const g = ensure(r.requestId);
        if (!g.request) g.request = r;
      });

      processes.forEach(r => {
        if (!r.requestId || r.requestId === '-') return;
        if (map.has(r.requestId)) map.get(r.requestId).process.push(r);
      });

      validations.forEach(r => {
        if (!r.requestId || r.requestId === '-') return;
        if (map.has(r.requestId)) map.get(r.requestId).validation.push(r);
      });

      errors.forEach(r => {
        if (!r.requestId || r.requestId === '-') return;
        if (map.has(r.requestId)) map.get(r.requestId).error.push(r);
      });

      // Renk hesapla + client-side filtreler
      const result = Array.from(map.values())
        .filter(g => {
          // Gizli endpoint filtresi
          const ep = g.request?.endpoint ?? '';
          if (GIZLI_ENDPOINTLER.some(gizli => ep === gizli)) return false;

          // Modül filtresi
          if (filtreler.modul) {
            const modul = g.request?.modul ?? '';
            if (modul !== filtreler.modul) return false;
          }

          // Arama filtresi — endpoint, islem, detay, hata açıklaması içinde arar
          if (filtreler.arama) {
            const q = filtreler.arama.toLowerCase();
            const aranacak = [
              g.request?.endpoint,
              g.request?.islem,
              g.request?.payload,
              ...g.process.map(p => p.detay),
              ...g.process.map(p => p.islem),
              ...g.error.map(e => e.hataAciklamasi),
              ...g.validation.map(v => v.hatalar),
            ].filter(Boolean).join(' ').toLowerCase();
            if (!aranacak.includes(q)) return false;
          }

          return true;
        })
        .map(g => {
        let renk = 'green';
        if (g.validation.length > 0) renk = 'yellow';
        if (g.error.length > 0)      renk = 'red';
        return { ...g, renk };
      });

      // Sırala
      result.sort((a, b) => {
        const za = a.request?.zaman ?? '0';
        const zb = b.request?.zaman ?? '0';
        if (filtreler.sirala === 'zaman_asc') return za.localeCompare(zb);
        return zb.localeCompare(za);
      });

      setAllGroups(result);
      setTotal(result.length);
      setLast(new Date());
    } catch (err) {
      setError(`Veriler alınamadı: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filtreKey]);

  useEffect(() => {
    fetch_();
    timerRef.current = setInterval(fetch_, 5 * 60 * 1000);
    return () => clearInterval(timerRef.current);
  }, [fetch_]);

  // Client-side sayfalama
  const toplamSayfa = Math.max(1, Math.ceil(allGroups.length / SAYFA_BOYUTU));
  const baslangic = (sayfa - 1) * SAYFA_BOYUTU;
  const sayfaData = allGroups.slice(baslangic, baslangic + SAYFA_BOYUTU);

  return {
    data: sayfaData,
    allData: allGroups,
    total,
    loading,
    error,
    lastFetch,
    refresh: fetch_,
    sayfa,
    setSayfa,
    toplamSayfa,
    sayfaBoyutu: SAYFA_BOYUTU,
  };
}
